/**
 * The Telegram Mini App side of the details form.
 *
 * Telegram opens a web_app button's URL with launch parameters in the hash:
 * `#tgWebAppData=<initData>&tgWebAppPlatform=...&tgWebAppThemeParams=...`.
 * `tgWebAppData` is the signed initData string the server verifies (HMAC with
 * the bot token); `URLSearchParams` decodes it exactly once, which yields the
 * raw query string Telegram signed. The official SDK is loaded only inside
 * Telegram, for ready/expand/theme, the closing confirmation and openLink.
 *
 * No MainButton: Telegram pins it over the bottom of the page, exactly where
 * the form's own "Save my details" sits, and a mis-tap closed the Mini App and
 * threw away typed PAN/KYC details. Telegram's own header already closes the
 * Mini App, and the closing confirmation makes that ask first.
 */

interface TelegramWebAppSdk {
  initData: string;
  themeParams: Record<string, string>;
  colorScheme?: 'light' | 'dark';
  ready(): void;
  expand(): void;
  close(): void;
  openLink(url: string): void;
  /** Opens a t.me link inside Telegram itself (the share sheet). Older clients lack it. */
  openTelegramLink?(url: string): void;
  enableClosingConfirmation?(): void;
  /** Bot API 8.0+: Telegram's own "save file" prompt. Older clients lack it (or throw WebAppMethodUnsupported). */
  downloadFile?(params: { url: string; file_name: string }, callback?: (accepted: boolean) => void): void;
  isVersionAtLeast?(version: string): boolean;
}

type TelegramWindow = Window & { Telegram?: { WebApp?: TelegramWebAppSdk } };

export const TELEGRAM_SDK_URL = 'https://telegram.org/js/telegram-web-app.js';

export interface TelegramLaunch {
  initData: string;
  platform: string;
  themeParams: Record<string, string>;
}

const COLOUR = /^#[0-9a-fA-F]{3,8}$/;

function sdkOrNull(): TelegramWebAppSdk | null {
  return (window as TelegramWindow).Telegram?.WebApp ?? null;
}

export function readTelegramLaunch(hash: string): TelegramLaunch {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  let themeParams: Record<string, string> = {};
  const rawTheme = params.get('tgWebAppThemeParams');
  if (rawTheme) {
    try {
      const parsed: unknown = JSON.parse(rawTheme);
      if (parsed && typeof parsed === 'object') themeParams = parsed as Record<string, string>;
    } catch {
      themeParams = {};
    }
  }
  return {
    initData: params.get('tgWebAppData') || sdkOrNull()?.initData || '',
    platform: params.get('tgWebAppPlatform') || '',
    themeParams,
  };
}

/** True when a #rgb / #rrggbb colour is dark (relative luminance below the midpoint). */
export function isDarkColour(value: string): boolean {
  let hex = value.replace('#', '');
  if (hex.length === 3 || hex.length === 4) hex = hex.slice(0, 3).split('').map((c) => c + c).join('');
  if (hex.length < 6) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.5;
}

/** True when the theme carries a usable page background, i.e. the page can paint in Telegram's colours. */
export function hasTelegramTheme(theme: Record<string, string>): boolean {
  return typeof theme.bg_color === 'string' && COLOUR.test(theme.bg_color);
}

/**
 * Take on the Telegram client's theme: its colours as `--tg-*` variables (the
 * page's Shell paints with them), and the app's own light/dark palette -- the
 * one the purchase panel's classes use -- switched to match Telegram's scheme.
 */
export function applyTheme(theme: Record<string, string>, colorScheme?: 'light' | 'dark'): void {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(theme)) {
    if (typeof value === 'string' && COLOUR.test(value)) {
      root.style.setProperty(`--tg-${key.replace(/_/g, '-')}`, value);
    }
  }
  const scheme = colorScheme ?? (hasTelegramTheme(theme) ? (isDarkColour(theme.bg_color) ? 'dark' : 'light') : null);
  if (scheme) root.classList.toggle('dark', scheme === 'dark');
  if (hasTelegramTheme(theme)) {
    document.body.style.backgroundColor = theme.bg_color;
  }
}

let sdkPromise: Promise<void> | null = null;

export function loadTelegramSdk(): Promise<void> {
  if (sdkOrNull()) return Promise.resolve();
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = TELEGRAM_SDK_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });
  }
  return sdkPromise;
}

export function startTelegramWebApp(): void {
  void loadTelegramSdk().then(() => {
    const sdk = sdkOrNull();
    if (!sdk) return;
    sdk.ready();
    sdk.expand();
    applyTheme(sdk.themeParams || {}, sdk.colorScheme);
    // Closing asks first, so a stray tap never discards details typed into the form.
    sdk.enableClosingConfirmation?.();
  });
}

/** Open a URL outside the Mini App (the insurer's payment page). False when not inside Telegram. */
export function openExternalLink(url: string): boolean {
  const sdk = sdkOrNull();
  if (!sdk) return false;
  sdk.openLink(url);
  return true;
}

/** Open a t.me link (e.g. the share sheet) inside Telegram; an older client falls back to openLink. False outside Telegram. */
export function openTelegramLink(url: string): boolean {
  const sdk = sdkOrNull();
  if (!sdk) return false;
  if (typeof sdk.openTelegramLink === 'function') sdk.openTelegramLink(url);
  else sdk.openLink(url);
  return true;
}

/**
 * Save a file from a Mini App (review C5). Telegram's Android/iOS webviews ignore
 * `<a download href="blob:...">`, so inside Telegram the file must be a real https URL
 * that works without the app's headers: `downloadFile` (Bot API 8.0+) shows Telegram's
 * native save prompt; an older client opens the URL in the external browser, which saves
 * it (the server sends it as an attachment). False when the SDK is not loaded. Anything
 * Telegram throws propagates, so the caller shows an error instead of going quiet.
 */
export function saveFileThroughTelegram(url: string, fileName: string): boolean {
  const sdk = sdkOrNull();
  if (!sdk) return false;
  if (typeof sdk.downloadFile === 'function' && sdk.isVersionAtLeast?.('8.0')) {
    sdk.downloadFile({ url, file_name: fileName });
  } else {
    sdk.openLink(url);
  }
  return true;
}

export function closeMiniApp(): void {
  sdkOrNull()?.close();
}
